import { useCallback, useEffect, useRef } from 'react'

interface UseLongPressOptions {
  delay?: number
  moveThreshold?: number
  onLongPress: () => void
  onClick?: () => void
}

export const useLongPress = ({
  delay = 500,
  moveThreshold = 20,
  onLongPress,
  onClick,
}: UseLongPressOptions) => {
  const timerRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const suppressClickRef = useRef(false)
  const startPointRef = useRef<{ x: number; y: number } | null>(null)
  const pressingRef = useRef(false)

  const lastTouchTimeRef = useRef(0)
  const TOUCH_MOUSE_GUARD_MS = 700

  const isRecentTouch = useCallback(() => {
    return Date.now() - lastTouchTimeRef.current < TOUCH_MOUSE_GUARD_MS
  }, [])

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const cancelPress = useCallback(() => {
    clearTimer()
    pressingRef.current = false
    startPointRef.current = null
  }, [clearTimer])

  const startPress = useCallback(
    (x: number, y: number) => {
      pressingRef.current = true
      longPressTriggeredRef.current = false
      startPointRef.current = { x, y }

      clearTimer()

      timerRef.current = window.setTimeout(() => {
        if (!pressingRef.current || !startPointRef.current) return

        longPressTriggeredRef.current = true
        suppressClickRef.current = true
        onLongPress()
      }, delay)
    },
    [clearTimer, delay, onLongPress]
  )

  const checkMove = useCallback(
    (x: number, y: number) => {
      if (!pressingRef.current || !startPointRef.current) return

      const dx = Math.abs(x - startPointRef.current.x)
      const dy = Math.abs(y - startPointRef.current.y)

      if (dx > moveThreshold || dy > moveThreshold) {
        cancelPress()
      }
    },
    [cancelPress, moveThreshold]
  )

  const finishPress = useCallback(() => {
    clearTimer()

    const shouldClick =
      pressingRef.current &&
      !longPressTriggeredRef.current &&
      !suppressClickRef.current

    pressingRef.current = false
    startPointRef.current = null
    longPressTriggeredRef.current = false

    if (shouldClick) {
      onClick?.()
    }

    window.setTimeout(() => {
      suppressClickRef.current = false
    }, 0)
  }, [clearTimer, onClick])

  useEffect(() => {
    const handleScroll = () => {
      if (pressingRef.current) {
        cancelPress()
      }
    }

    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [cancelPress])

  return {
    onTouchStart: (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return
      const touch = e.touches[0]
      if (!touch) return

      lastTouchTimeRef.current = Date.now()
      startPress(touch.clientX, touch.clientY)
    },

    onTouchMove: (e: React.TouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return
      checkMove(touch.clientX, touch.clientY)
    },

    onTouchEnd: (e: React.TouchEvent) => {
      lastTouchTimeRef.current = Date.now()

      if (longPressTriggeredRef.current) {
        e.preventDefault()
      }

      finishPress()
    },

    onTouchCancel: () => {
      lastTouchTimeRef.current = Date.now()
      cancelPress()
    },

    onMouseDown: (e: React.MouseEvent) => {
      if (isRecentTouch()) return
      if (e.button !== 0) return
      startPress(e.clientX, e.clientY)
    },

    onMouseMove: (e: React.MouseEvent) => {
      if (isRecentTouch()) return
      checkMove(e.clientX, e.clientY)
    },

    onMouseUp: (e: React.MouseEvent) => {
      if (isRecentTouch()) return

      if (longPressTriggeredRef.current) {
        e.preventDefault()
      }

      finishPress()
    },

    onMouseLeave: () => {
      if (isRecentTouch()) return
      cancelPress()
    },

    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    },

    onDragStart: (e: React.DragEvent) => {
      e.preventDefault()
    },

    onClickCapture: (e: React.MouseEvent) => {
      if (suppressClickRef.current) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
  }
} 